import { Paper } from "@mui/material";
import Box from "@mui/material/Box/Box";
import DOMPurify from "dompurify";
import "react-quill/dist/quill.snow.css";

const trustedProviders = [
	// YouTube variations
	"youtube.com",
	"youtu.be",
	"youtube-nocookie.com",

	// Vimeo variations
	"vimeo.com",
	"player.vimeo.com",

	// Lecture Capture / Academic platforms
	"panopto.com",
	"echo360.org",
	"echo360.org.uk",
	"echo360.edu.au",
	"kanopy.com",

	"sharepoint.com",
	"microsoftstream.com",
];

DOMPurify.addHook("uponSanitizeElement", (node, data) => {
	if (node instanceof Element && data.tagName === "iframe") {
		const src = node.getAttribute("src") || "";

		// Explicitly allow local media blobs if applicable
		if (src.startsWith("blob:")) {
			return;
		}

		try {
			const parsedUrl = new URL(src);

			// Enforce HTTPS strictly (prevent javascript: or insecure http:)
			if (parsedUrl.protocol !== "https:") {
				throw new Error("Insecure protocol");
			}

			const hostname = parsedUrl.hostname.toLowerCase();

			// Validate hostname against trusted providers explicitly
			const isTrusted = trustedProviders.some((provider) => {
				// Exact match (e.g., "youtube.com") OR subdomain match (e.g., "player.vimeo.com")
				return hostname === provider || hostname.endsWith(`.${provider}`);
			});

			if (!isTrusted) {
				throw new Error("Untrusted provider");
			}
		} catch (e) {
			// If URL parsing fails, or validation checks throw -> Destructive removal
			if (node.parentNode) {
				node.parentNode.removeChild(node);
			}
		}
	}
});

type InstructionsPreviewProps = {
	instruction: string;
	/**@default 400px */
	height?: number | string;
};

export default function InstructionsPreview({ instruction, height = "400px" }: InstructionsPreviewProps) {
	const cleanHTML = DOMPurify.sanitize(instruction, {
		ADD_TAGS: ["iframe"], // Tell DOMPurify not to delete iframes
		ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "src"], // Allow necessary video player attributes
	});

	return (
		<Paper
			sx={{
				bgcolor: "white",
				border: "0.33px solid #B3B3B3",
				borderRadius: "10px",
				boxShadow: "0px 2.64px 5.93px 0px rgba(0, 0, 0, 0.1)",
				height: "100%",
				width: "100%",
			}}
		>
			<Box sx={{ maxHeight: height, overflow: "auto" }}>
				<div
					className="ql-editor"
					ref={(el) => {
						if (el) {
							el.innerHTML = cleanHTML;
							el.querySelectorAll("[substitution-tag]").forEach((tag) => {
								tag.removeAttribute("style");
							});
						}
					}}
				/>
			</Box>
		</Paper>
	);
}
